import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaArrowLeft, FaArrowRight, FaQuoteLeft } from 'react-icons/fa';
import { getProjectBySlug, projects } from '../data/projects';

const PortfolioDetail = () => {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  if (!project) {
    return <Navigate to="/portfolio" replace />;
  }

  // Get next and previous projects
  const currentIndex = projects.findIndex(p => p.slug === slug);
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;

  // Schema.org Case Study markup
  const caseStudySchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": project.title,
    "description": project.after.situation,
    "author": {
      "@type": "Organization",
      "name": "Olney Tech Solutions"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Olney Tech Solutions"
    },
    "datePublished": `${project.year}-01-01`
  };

  return (
    <>
      <Helmet>
        <title>{project.metaTitle}</title>
        <meta name="description" content={project.metaDescription} />
        <link rel="canonical" href={`https://olney.dev/portfolio/${project.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(caseStudySchema)}
        </script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="pt-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <span>/</span>
            <Link to="/portfolio" className="hover:text-white transition-colors">Portfolio</Link>
            <span>/</span>
            <span className="text-white">{project.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[var(--primary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
              >
                <FaArrowLeft /> Retour au portfolio
              </Link>

              <span className="text-[var(--secondary)] text-sm font-bold uppercase tracking-widest block mb-4">
                {project.category}
              </span>

              <h1 className="text-4xl sm:text-5xl font-bold mb-6 font-['Space_Grotesk'] text-white">
                {project.title}
              </h1>

              <div className="flex flex-wrap gap-4 text-gray-400 text-sm mb-8">
                <span>🏢 {project.client}</span>
                <span>👥 {project.clientSize}</span>
                <span>📍 {project.location}</span>
                <span>📅 {project.year}</span>
                <span>⏱️ {project.duration}</span>
              </div>

              {/* Results Summary */}
              <div className="grid grid-cols-3 gap-4">
                {project.results.map((result, idx) => (
                  <div key={idx} className="glass-panel rounded-2xl p-4 text-center">
                    <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)]">
                      {result.metric}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">{result.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="aspect-video rounded-3xl bg-gradient-to-br from-[var(--primary)]/20 to-[var(--secondary)]/20 overflow-hidden border border-white/10">
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BAB Framework Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              La Transformation
            </h2>
          </motion.div>

          <div className="space-y-8">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-panel rounded-3xl p-8 border-l-4 border-red-500"
            >
              <h3 className="text-red-400 font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                <span className="text-xl">❌</span> Avant - Le Problème
              </h3>
              <p className="text-xl text-white mb-4 font-medium">
                {project.before.situation}
              </p>
              <p className="text-gray-400 mb-4">
                {project.before.pain}
              </p>
              <div className="flex flex-wrap gap-3">
                {project.before.metrics.map((metric, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-sm">
                    {metric}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Bridge */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-panel rounded-3xl p-8 border-l-4 border-[var(--secondary)]"
            >
              <h3 className="text-[var(--secondary)] font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                <span className="text-xl">🔧</span> Notre Approche
              </h3>
              <p className="text-xl text-white mb-6 font-medium">
                {project.bridge.approach}
              </p>
              <div className="space-y-3">
                {project.bridge.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[var(--secondary)]/20 text-[var(--secondary)] flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      {idx + 1}
                    </span>
                    <span className="text-gray-300">{step}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-panel rounded-3xl p-8 border-l-4 border-[var(--success)]"
            >
              <h3 className="text-[var(--success)] font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                <span className="text-xl">✅</span> Après - Les Résultats
              </h3>
              <p className="text-xl text-white mb-4 font-medium">
                {project.after.situation}
              </p>
              <p className="text-gray-400 mb-4">
                {project.after.gain}
              </p>
              <div className="flex flex-wrap gap-3">
                {project.after.metrics.map((metric, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-[var(--success)]/10 text-[var(--success)] text-sm">
                    {metric}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results Detail */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Résultats Mesurables
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {project.results.map((result, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-panel rounded-3xl p-8 text-center"
              >
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] mb-4">
                  {result.metric}
                </div>
                <div className="text-white font-bold text-lg mb-2">{result.label}</div>
                <div className="text-gray-400 text-sm">{result.detail}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-8 font-['Space_Grotesk']">
              Technologies Utilisées
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {project.technologies.map((tech, idx) => (
                <span
                  key={idx}
                  className="px-6 py-3 rounded-xl bg-[var(--primary)]/10 border border-[var(--primary)]/30 text-[var(--primary)] font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl p-12 border-[var(--primary)]/30 relative overflow-hidden"
          >
            <div className="absolute top-8 left-8 text-[var(--primary)]/20">
              <FaQuoteLeft size={60} />
            </div>

            <div className="relative z-10 text-center">
              <p className="text-2xl text-white italic mb-8 leading-relaxed">
                "{project.testimonial.quote}"
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-white font-bold">
                  {project.testimonial.author.charAt(0)}
                </div>
                <div className="text-left">
                  <div className="text-white font-bold">{project.testimonial.author}</div>
                  <div className="text-gray-400 text-sm">{project.testimonial.role}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      {project.images && project.images.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
                Aperçu du Projet
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {project.images.map((img, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="aspect-video rounded-2xl bg-gradient-to-br from-[var(--primary)]/20 to-[var(--secondary)]/20 overflow-hidden border border-white/10"
                >
                  <img
                    src={img}
                    alt={`${project.title} - Screenshot ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-500">Aperçu à venir</div>';
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Navigation */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            {prevProject ? (
              <Link
                to={`/portfolio/${prevProject.slug}`}
                className="flex items-center gap-4 text-gray-400 hover:text-white transition-colors group"
              >
                <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                <div>
                  <div className="text-xs uppercase tracking-wider mb-1">Projet précédent</div>
                  <div className="font-bold">{prevProject.title}</div>
                </div>
              </Link>
            ) : <div />}

            {nextProject ? (
              <Link
                to={`/portfolio/${nextProject.slug}`}
                className="flex items-center gap-4 text-gray-400 hover:text-white transition-colors group text-right"
              >
                <div>
                  <div className="text-xs uppercase tracking-wider mb-1">Projet suivant</div>
                  <div className="font-bold">{nextProject.title}</div>
                </div>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl p-12 text-center border-[var(--primary)]/30"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
              Votre projet pourrait être le prochain
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Discutons de vos objectifs et créons ensemble des résultats similaires.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl"
              >
                Démarrer mon projet
              </Link>
              <Link
                to="/portfolio"
                className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
              >
                Voir d'autres projets
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PortfolioDetail;
